import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Lock, Mail, ChevronLeft } from "lucide-react";

const VARIANTS = [
  {
    id: "soubor",
    name: "Soubor Hub",
    mark: "So",
    tagline: "One hub for every moving part of your business.",
    sub: "Your workspace for apps, projects, invoices and support. Tied to Soubor's backbone.",
    accent: { h: 231, s: 75, l: 50 },     // deep indigo-blue
    accentName: "Indigo",
    badge: "Parent brand · Ownable · One word",
    note: "Customers type one word. \"Your Soubor is live.\"",
    footer: "© 2026 Soubor Labs",
  },
  {
    id: "console",
    name: "SAL Console",
    mark: "S",
    tagline: "Control every corner of your business from one console.",
    sub: "Premium, unambiguous. Keeps SAL as your product family and Console as the control surface.",
    accent: { h: 222, s: 100, l: 50 },    // electric cobalt (current)
    accentName: "Cobalt",
    badge: "Product-family friendly · Scales",
    note: "Lets you spin up SAL Pay, SAL Ops, SAL Inbox later.",
    footer: "© 2026 SAL Labs",
  },
  {
    id: "beacon",
    name: "Beacon",
    mark: "B",
    tagline: "See every signal across your account, in one light.",
    sub: "Single-word, memorable, customer-first. Feels like the customer's space, not a vendor tool.",
    accent: { h: 36, s: 95, l: 52 },      // warm amber/gold
    accentName: "Amber",
    badge: "Single-word · Premium · Customer-led",
    note: "Easy to say. Easy to remember. Hard to forget.",
    footer: "Powered by SAL · © 2026",
  },
];

export default function BrandPreview() {
  const { user } = useAuth();
  const [selected, setSelected] = useState(null);
  if (user) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text))] relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))]">Brand preview</div>
            <h1 className="text-[32px] font-bold tracking-tight mt-1">Pick a name. See it live.</h1>
            <p className="text-[14px] text-[hsl(var(--text-muted))] mt-1">Three takes on your customer-facing brand. Hover, compare, pick the one that clicks.</p>
          </div>
          <a href="/login" className="text-[13px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] inline-flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" strokeWidth={1.6}/>Back to login
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {VARIANTS.map((v) => (
            <VariantCard key={v.id} v={v} active={selected === v.id} onSelect={() => setSelected(v.id)} />
          ))}
        </div>

        {selected && (
          <div className="mt-10 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))]">You picked</div>
              <div className="text-[22px] font-bold mt-0.5">{VARIANTS.find((x) => x.id === selected).name}</div>
            </div>
            <div className="text-[13px] text-[hsl(var(--text-muted))] max-w-md">
              Tell me to ship this across the app. Wordmark, login, sidebar, tab title, emails, invoice template.
              I can have it swapped in a single pass.
            </div>
          </div>
        )}

        <div className="mt-10 text-[11px] text-[hsl(var(--text-muted))] leading-relaxed max-w-3xl">
          <strong className="text-[hsl(var(--text))]">Tip:</strong> say the name out loud to a future customer:
          "We use <em>{VARIANTS.find((x) => x.id === selected)?.name || "______"}</em> for everything with Soubor."
          Which one rolls off easiest?
        </div>
      </div>
    </div>
  );
}

function VariantCard({ v, active, onSelect }) {
  const accent = `${v.accent.h} ${v.accent.s}% ${v.accent.l}%`;
  const accentHover = `${v.accent.h} ${v.accent.s}% ${Math.max(v.accent.l - 10, 10)}%`;
  const accentMuted = `${v.accent.h} ${v.accent.s}% 96%`;
  return (
    <div
      onClick={onSelect}
      className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all ${active ? "ring-2 ring-offset-4 ring-offset-[hsl(var(--background))]" : "hover:-translate-y-[2px]"}`}
      style={active ? { "--ring-color": `hsl(${accent})`, boxShadow: `0 0 0 2px hsl(${accent})`, ringColor: `hsl(${accent})` } : {}}
      data-testid={`brand-${v.id}`}
    >
      {/* Glow bg */}
      <div className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full pointer-events-none opacity-60"
           style={{ background: `radial-gradient(circle, hsl(${accent} / 0.20), transparent 60%)` }} />

      <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-2xl p-6">
        {/* Header bar */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold font-mono text-[14px]"
              style={{
                background: `linear-gradient(135deg, hsl(${accent}), hsl(${accentHover}))`,
                boxShadow: `0 6px 18px -4px hsl(${accent} / 0.45), inset 0 1px 0 rgba(255,255,255,0.25)`,
              }}
            >
              {v.mark}
            </div>
            <div className="font-bold tracking-tight text-[17px]">{v.name}</div>
          </div>
          <div
            className="text-[9px] uppercase tracking-widest font-semibold px-2 py-1 rounded"
            style={{ background: `hsl(${accentMuted})`, color: `hsl(${accent})` }}
          >
            {v.accentName}
          </div>
        </div>

        {/* Mini hero */}
        <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold mb-1.5">Welcome back</div>
        <div className="text-[22px] font-bold tracking-tight leading-[1.15] mb-2">
          Sign in to <span style={{ color: `hsl(${accent})` }}>{v.name}</span>
        </div>
        <div className="text-[12px] text-[hsl(var(--text-muted))] leading-relaxed mb-5 min-h-[52px]">{v.sub}</div>

        {/* Mock form */}
        <div className="space-y-2 mb-4">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(var(--text-subtle))]" strokeWidth={1.6}/>
            <div className="w-full h-9 pl-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[12px] text-[hsl(var(--text-subtle))] flex items-center">you@company.com</div>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(var(--text-subtle))]" strokeWidth={1.6}/>
            <div className="w-full h-9 pl-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[12px] text-[hsl(var(--text-subtle))] flex items-center">••••••••</div>
          </div>
        </div>
        <div
          className="h-10 rounded-lg flex items-center justify-center gap-2 text-white font-semibold text-[13px] transition-transform group-hover:scale-[1.01]"
          style={{
            background: `linear-gradient(135deg, hsl(${accent}), hsl(${accentHover}))`,
            boxShadow: `0 8px 24px -6px hsl(${accent} / 0.55)`,
          }}
        >
          Sign in <ArrowRight className="w-3.5 h-3.5" strokeWidth={2}/>
        </div>

        {/* Why this works */}
        <div className="mt-5 pt-5 border-t border-[hsl(var(--border))]">
          <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold mb-2">Why this works</div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {v.badge.split(" · ").map((b) => (
              <span key={b} className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: `hsl(${accentMuted})`, color: `hsl(${accent})` }}>{b}</span>
            ))}
          </div>
          <div className="text-[11px] text-[hsl(var(--text-muted))] italic leading-relaxed">{v.note}</div>
        </div>

        {/* Footer line */}
        <div className="mt-5 text-[10px] text-[hsl(var(--text-subtle))] uppercase tracking-widest">{v.footer}</div>

        {/* Pick indicator */}
        <div className="mt-4">
          <div
            className={`h-9 rounded-lg border flex items-center justify-center text-[12px] font-semibold transition-all ${active ? "text-white" : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"}`}
            style={active
              ? { background: `hsl(${accent})`, borderColor: `hsl(${accent})` }
              : { borderColor: "hsl(var(--border))" }
            }
            data-testid={`pick-${v.id}`}
          >
            {active ? "Selected ✓" : `Pick ${v.name}`}
          </div>
        </div>
      </div>
    </div>
  );
}
